import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export default function SelectDate({ type, onDateRangeChange }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedRange, setSelectedRange] = useState('last_6'); 
  const [rangeLabel, setRangeLabel] = useState('Derniers 6 mois');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const formatDateForAPI = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  useEffect(() => {
    handleQuickFilter('last_30'); 
  }, []);

  const handleQuickFilter = (filter) => {
    setSelectedRange(filter);

    if (filter === 'custom') {
      setStartDate(null);
      setEndDate(null);
      setRangeLabel('Sélectionnez une période');
      return; 
    }
    
    const today = new Date();
    let start, end, label;

    switch(filter) {
      case 'today':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        label = "Aujourd'hui";
        break;
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        label = "Ce mois";
        break;
      case 'last_30':
        start = new Date();
        start.setDate(today.getDate() - 30);
        end = today;
        label = 'Derniers 30 jours';
      break;
      case 'last_6':
        start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0); 
        label = 'Derniers 6 mois';
        break;
      case 'last_12':
        start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0); 
        label = 'Derniers 12 mois';
        break;
      case 'all':
        start = new Date(2025, 9, 1); 
        end = today;
        label = 'Toute la période';
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
    setRangeLabel(label);
    
    if (onDateRangeChange) {
      onDateRangeChange({
        start_date: formatDateForAPI(start),
        end_date: formatDateForAPI(end),
        type: filter
      });
    }
    
    setShowCalendar(false);
  };

  const handleDateClick = (day, month, year) => {
    if (selectedRange !== 'custom') return;

    const clickedDate = new Date(year, month, day);
    
    if (!startDate || (startDate && endDate)) {
      setStartDate(clickedDate);
      setEndDate(null);
    } else if (clickedDate < startDate) {
      setEndDate(startDate);
      setStartDate(clickedDate);
    } else {
      setEndDate(clickedDate);
    }
  };

  const applyDateRange = () => {
    if (!startDate || !endDate) return;
    
    const finalStartDate = startDate < endDate ? startDate : endDate;
    const finalEndDate = startDate > endDate ? startDate : endDate;
    
    setStartDate(finalStartDate);
    setEndDate(finalEndDate);
    setRangeLabel(`${formatDate(finalStartDate)} - ${formatDate(finalEndDate)}`);
    
    if (onDateRangeChange) {
      onDateRangeChange({
        start_date: formatDateForAPI(finalStartDate),
        end_date: formatDateForAPI(finalEndDate),
        type: 'custom'
      });
    }
    
    setShowCalendar(false);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { 
      daysInMonth: lastDay.getDate(), 
      startingDayOfWeek: firstDay.getDay(), 
      year, 
      month 
    };
  };

  const isDateInRange = (day, month, year) => {
    if (!startDate) return false;
    const date = new Date(year, month, day).setHours(0, 0, 0, 0);
    const normStart = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const normEnd = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;

    if (!endDate) return date === normStart;
    
    return date >= Math.min(normStart, normEnd) && date <= Math.max(normStart, normEnd);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + direction,
      1
    ));
  };

  const renderCalendar = (monthOffset = 0, showControls = false) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(date);
    const days = [];
    const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="text-center py-1 text-gray-400"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isInRange = isDateInRange(day, month, year);
      
      const isStart = startDate && day === startDate.getDate() && month === startDate.getMonth() && year === startDate.getFullYear();
      const isEnd = endDate && day === endDate.getDate() && month === endDate.getMonth() && year === endDate.getFullYear();
      const isSingleSelected = isStart && !endDate;

      days.push(
        <div
          key={day}
          onClick={() => selectedRange === 'custom' && handleDateClick(day, month, year)} 
          className={`text-center py-1 text-xs transition-all ${
            selectedRange === 'custom' ? 'cursor-pointer' : 'cursor-default'
          } ${
            isSingleSelected || (isStart && isEnd) ? 'bg-[var(--color-primary-500)] text-white rounded-full font-semibold' :
            isStart ? 'bg-[var(--color-primary-500)] text-white rounded-l-full font-semibold' :
            isEnd ? 'bg-[var(--color-primary-500)] text-white rounded-r-full font-semibold' :
            isInRange ? 'bg-[var(--color-primary-100)] text-[var(--color-primary-500)]' :
            selectedRange === 'custom' ? 'hover:bg-gray-100 rounded-md' : ''
          }`}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          {showControls && (
            <button
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
          )}
          <h3 className={`text-sm font-bold text-gray-800 ${!showControls && 'mx-auto'}`}>
            {monthNames[month]} {year}
          </h3>
          {showControls && (
            <button
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          )}
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-semibold text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    );
  };
  
  return (
    <div className="relative mt-3 max-xl:mt-0 sm:mt-0">
      <button
        onClick={() => setShowCalendar(!showCalendar)}
        className={`flex max-xl:w-full items-center gap-2 px-4 py-2.5 bg-white w-50 ${type ? 'rounded-full' : 'rounded-xl'} cursor-pointer transition-all shadow-sm`}
      >
        <Calendar size={15} className="text-gray-900" />
        <span className="text-xs text-gray-700 text-nowrap">{rangeLabel}</span>
      </button>

      {showCalendar && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowCalendar(false)}
          />
          
          <div className="absolute right-0 top-full mt-3 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col md:flex-row max-w-lg md:max-w-none">
            
            <div className="border-r border-gray-100 bg-gray-50 p-4 w-full md:w-52">
              <h4 className="text-xs text-gray-500 uppercase mb-3 px-2">Raccourcis</h4>
              {['today', 'this_month', 'last_30', 'last_6', 'last_12', 'all', 'custom'].map(filter => (
                <button
                  key={filter}
                  onClick={() => handleQuickFilter(filter)}
                  className={`w-full text-left px-3 py-2.5 text-xs rounded-lg transition-all mb-1 ${
                    selectedRange === filter ? 'bg-[var(--color-primary-500)] text-white' : 'hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {filter === 'today' ? "Aujourd'hui" :
                   filter === 'this_month' ? "Ce mois" :
                   filter === 'last_30' ? "Derniers 30 jours" :
                   filter === 'last_6' ? 'Derniers 6 mois' :
                   filter === 'last_12' ? 'Derniers 12 mois' :
                   filter === 'all' ? 'Toute la période' : 'Période personnalisée'}
                </button>
              ))}
            </div>
            
            <div>
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-60 border-b md:border-b-0 md:border-r border-gray-200">
                  {renderCalendar(0, true)}
                </div>
                <div className="w-full md:w-60">
                  {renderCalendar(1)}
                </div>
              </div>

              <div className="border-t border-gray-200 p-4 bg-gray-50 flex flex-col sm:flex-row justify-between items-center w-full">
                <div className="text-sm text-gray-600 font-medium mb-3 sm:mb-0">
                  {startDate && endDate ? (
                    <>
                      <span className='text-xs'>{formatDate(startDate)}</span>
                      <span className='ml-1'> - <span className='text-xs'>{formatDate(endDate)}</span></span>
                    </>
                  ) : startDate ? (
                    <span className='font-bold text-xs text-[var(--color-primary-500)]'>{formatDate(startDate)}</span>
                  ) : (
                    'Sélectionnez une période'
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => { 
                      setShowCalendar(false); 
                    }}
                    className="px-4 py-2 text-xs border-2 cursor-pointer border-gray-300 rounded-xl hover:bg-gray-100 transition-colors font-semibold text-gray-700"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={applyDateRange}
                    disabled={selectedRange !== 'custom' || !startDate || !endDate}
                    className={`px-4 py-2 text-xs rounded-xl font-bold transition-colors ${
                      selectedRange === 'custom' && startDate && endDate ? 'bg-[var(--color-primary-500)] text-white hover:bg-[var(--color-primary-300)] shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
